/**
 * Labyrinth Blog Post Component
 * Handles image switching and metadata updates for digital archive posts
 * @typedef {import('./global.d.ts')}
 */

/**
 * Dataset shape for each thumbnail button
 * @typedef {Object} LabyrinthBlogPostThumbnailDataset
 * @property {string} [imageUrl]
 * @property {string} [imageAlt]
 * @property {string} [imageSrcset]
 * @property {string} [itemText]
 * @property {string} [nameText]
 */

if (!customElements.get('labyrinth-blog-post-component')) {
    class LabyrinthBlogPostComponent extends HTMLElement {
        constructor() {
            super();
            /** @type {HTMLImageElement | null} */
            this.mainImage = null;
            /** @type {NodeListOf<HTMLButtonElement> | null} */
            this.thumbnails = null;
            /** @type {NodeListOf<HTMLButtonElement> | null} */
            this.modalThumbnails = null;
            /** @type {HTMLElement | null} */
            this.itemValue = null;
            /** @type {HTMLElement | null} */
            this.nameValue = null;
            /** @type {NodeListOf<HTMLButtonElement> | null} */
            this.controlButtons = null;
            /** @type {((event: KeyboardEvent) => void) | null} */
            this.keyDownHandler = null;
            /** @type {number} */
            this.currentIndex = 0;
            /** @type {boolean} */
            this.isTransitioning = false;
            /** @type {number | null} */
            this.pendingImageUpdate = null;
        }

        connectedCallback() {
            // Get DOM elements
            this.mainImage = this.querySelector('.labyrinth-blog-post__image');
            const allThumbnails = Array.from(this.querySelectorAll('.labyrinth-blog-post__thumbnail'));

            this.thumbnails = [];
            this.modalThumbnails = [];

            allThumbnails.forEach(thumbnail => {
                if (thumbnail.closest('.modal__content')) {
                    this.modalThumbnails.push(thumbnail);
                } else {
                    this.thumbnails.push(thumbnail);
                }
            });

            this.itemValue = this.querySelector('[ref="itemValue"]');
            this.nameValue = this.querySelector('[ref="nameValue"]');
            this.controlButtons = this.querySelectorAll('.labyrinth-blog-post__control-button');

            if (!this.thumbnails || this.thumbnails.length === 0) return;

            if (this.thumbnails && this.thumbnails.length > 0) {
                this.thumbnails.forEach((thumbnail) => {
                    const srcset = /** @type {string | undefined} */ (thumbnail.dataset.imageSrcset);
                    const src = /** @type {string | undefined} */ (thumbnail.dataset.imageUrl);

                    if (srcset) {
                        /** @type {HTMLImageElement} */
                        const img = new Image();
                        img.sizes = "100vw";
                        img.srcset = srcset;
                        img.src = src; // Fallback
                    }
                });
            }

            this.setupEventListeners();
        }

        disconnectedCallback() {
            if (this.keyDownHandler) {
                document.removeEventListener('keydown', this.keyDownHandler);
            }
        }

        setupEventListeners() {
            if (!this.thumbnails) return;

            // Thumbnail click handlers
            this.thumbnails.forEach((thumbnail, index) => {
                thumbnail.addEventListener('click', () => {
                    this.switchToImage(thumbnail, index);
                });
            });

            this.modalThumbnails?.forEach((thumbnail, index) => {
                thumbnail.addEventListener('click', () => {
                    this.switchToImage(thumbnail, index);
                })
            })

            // Control button handlers (Previous/Next)
            const prevButton = this.controlButtons?.[0];
            const nextButton = this.controlButtons?.[1];

            if (prevButton && nextButton) {
                prevButton.addEventListener('click', () => this.previousImage());
                nextButton.addEventListener('click', () => this.nextImage());
            }

            // Keyboard navigation
            this.keyDownHandler = (event) => {
                if (event.key === 'ArrowLeft') {
                    this.previousImage();
                } else if (event.key === 'ArrowRight') {
                    this.nextImage();
                }
            }

            document.addEventListener('keydown', this.keyDownHandler)

            /** @type {number} */
            let initialX = 0;
            /** @type {number} */
            let initialY = 0;
            /** @type {number} */
            let initialTime = 0;

            this.mainImage?.addEventListener('touchstart', (e) => {
                initialX = e.touches[0]?.clientX ?? 0;
                initialY = e.touches[0]?.clientY ?? 0;
                initialTime = Date.now();
            });

            this.mainImage?.addEventListener('touchend', (e) => {
                const touch = e.changedTouches && e.changedTouches[0];
                if (!touch || !this.thumbnails || this.thumbnails.length === 0) return;

                const deltaX = touch.clientX - initialX;
                const deltaY = Math.abs(touch.clientY - initialY);
                const deltaTime = Date.now() - initialTime;

                // Configs for swipe detection
                const MIN_SWIPE_DISTANCE = 30;
                const MAX_VERTICAL_VARIANCE = 100;
                const MAX_SWIPE_TIME = 300;

                if (deltaX <= -MIN_SWIPE_DISTANCE && deltaY <= MAX_VERTICAL_VARIANCE && deltaTime <= MAX_SWIPE_TIME) {
                    const nextIndex = (this.currentIndex + 1) % this.thumbnails.length;
                    const nextThumb = this.thumbnails[nextIndex];
                    if (nextThumb) {
                        this.switchToImage(nextThumb, nextIndex);
                    }
                }
                else if (deltaX >= MIN_SWIPE_DISTANCE && deltaY <= MAX_VERTICAL_VARIANCE && deltaTime <= MAX_SWIPE_TIME) {
                    const prevIndex = (this.currentIndex - 1 + this.thumbnails.length) % this.thumbnails.length;
                    const prevThumb = this.thumbnails[prevIndex];
                    if (prevThumb) {
                        this.switchToImage(prevThumb, prevIndex);
                    }
                }
            });
        }

        /**
         * @param {HTMLButtonElement | HTMLImageElement} thumbnail
         * @param {number} index
         */
        switchToImage(thumbnail, index) {
            if (!this.mainImage || !this.thumbnails) return;
            if (index === this.currentIndex) return;

            // Get image and metadata from thumbnail
            const imageUrl = /** @type {string | undefined} */ (thumbnail.dataset.imageUrl);
            const imageAlt = /** @type {string | undefined} */ (thumbnail.dataset.imageAlt);
            const imageSrcset = /** @type {string | undefined} */ (thumbnail.dataset.imageSrcset);
            const itemText = /** @type {string | undefined} */ (thumbnail.dataset.itemText);
            const nameText = /** @type {string | undefined} */ (thumbnail.dataset.nameText);

            if (!imageUrl) return;

            // Cancel any pending update
            if (this.pendingImageUpdate) {
                clearTimeout(this.pendingImageUpdate);
            }

            // Only add fade-out if not already transitioning
            if (!this.isTransitioning) {
                this.mainImage.classList.add('fade-out');
                this.isTransitioning = true;
            }

            this.pendingImageUpdate = setTimeout(() => {
                const img = this.mainImage;
                if (!img) return;

                // Clear any existing onload handler
                img.onload = null;

                img.onload = () => {
                    img.classList.remove('fade-out');
                    this.isTransitioning = false;
                    img.onload = null;
                };

                img.src = imageUrl;
                img.alt = imageAlt || '';
                if (imageSrcset) {
                    img.srcset = imageSrcset;
                }

                this.pendingImageUpdate = null;
            }, 250);

            // Update metadata
            if (this.itemValue && itemText !== undefined) {
                this.itemValue.textContent = itemText;
            }
            if (this.nameValue && nameText !== undefined) {
                this.nameValue.textContent = nameText;
            }

            // Update thumbnail states
            const currentThumbnail = [this.thumbnails[this.currentIndex], this.modalThumbnails[this.currentIndex]]
            const nextThumbnail = [this.modalThumbnails[index], this.thumbnails[index]];

            if (currentThumbnail) {
                currentThumbnail.forEach(thumbnail => {
                    if (thumbnail) {
                        thumbnail.classList.remove('labyrinth-blog-post__thumbnail--active');
                        thumbnail.setAttribute('aria-selected', 'false');
                    }
                });
            }

            if (nextThumbnail) {
                nextThumbnail.forEach(thumbnail => {
                    if (thumbnail) {
                        thumbnail.classList.add('labyrinth-blog-post__thumbnail--active');
                        thumbnail.setAttribute('aria-selected', 'true');
                    }
                });
            }

            // Scroll thumbnail into view
            this.scrollThumbnailIntoView(thumbnail);

            this.currentIndex = index;
        }


        /**
         * @param {HTMLButtonElement} thumbnail
         */
        scrollThumbnailIntoView(thumbnail) {
            const container = thumbnail.closest('.labyrinth-blog-post__thumbnails');

            if (!thumbnail || !container) return;

            const thumbnailLeft = thumbnail.offsetLeft;
            const thumbnailWidth = thumbnail.offsetWidth;
            const containerScrollLeft = container.scrollLeft;
            const containerWidth = container.clientWidth;

            // Calculate if thumbnail is outside visible area
            const thumbnailRight = thumbnailLeft + thumbnailWidth;
            const visibleRight = containerScrollLeft + containerWidth;

            if (thumbnailLeft < containerScrollLeft) {
                // Thumbnail is to the left of visible area
                container.scrollLeft = thumbnailLeft - 8; // 8px padding
            } else if (thumbnailRight > visibleRight) {
                // Thumbnail is to the right of visible area
                container.scrollLeft = thumbnailRight - containerWidth + 8; // 8px padding
            }
        }

        /**
         * Navigate to the next image
         */
        nextImage() {
            if (!this.thumbnails || this.thumbnails.length === 0) return;

            const nextIndex = (this.currentIndex + 1) % this.thumbnails.length;
            const nextThumbnail = this.thumbnails[nextIndex];

            if (nextThumbnail) {
                this.switchToImage(nextThumbnail, nextIndex);
            }
        }

        /**
         * Navigate to the previous image
         */
        previousImage() {
            if (!this.thumbnails || this.thumbnails.length === 0) return;

            const prevIndex = (this.currentIndex - 1 + this.thumbnails.length) % this.thumbnails.length;
            const prevThumbnail = this.thumbnails[prevIndex];

            if (prevThumbnail) {
                this.switchToImage(prevThumbnail, prevIndex);
            }
        }
    }

    customElements.define('labyrinth-blog-post-component', LabyrinthBlogPostComponent);
}

/**
 * Calculate which thumbnails fit in the thumbnails container and show/hide the "view more" button.
 * @returns {void}
 */
function calculateThumbnailsVisibility() {
    if (window.innerWidth < 1024) {
        const viewMoreButton = document.getElementById('view-more-button');
        if (viewMoreButton) viewMoreButton.style.display = 'none';
        return;
    }

    const container = document.querySelector('.labyrinth-blog-post__thumbnails');
    const thumbnailsList = document.getElementById('labyrinth-thumbnails');
    const headerComponent = document.querySelector('#header-component');
    if (!container || !thumbnailsList) return;

    const viewMoreBtn = container.querySelector('.labyrinth-blog-post__view-more');
    const btnHeight = viewMoreBtn ? viewMoreBtn.offsetHeight : 0;

    const gapStr = getComputedStyle(thumbnailsList).getPropertyValue('gap').trim();
    const gapValue = parseFloat(gapStr);

    const availableHeight = container.offsetHeight - btnHeight - (headerComponent ? headerComponent.offsetHeight : 0);

    // Filter only thumbnail card nodes
    const thumbnailNodes = Array.from(thumbnailsList.children).filter(node =>
        node.classList.contains('labyrinth-blog-post__thumbnail-wrapper')
    );

    // Measure the first card only (assume all same height)
    let cardHeight = 0;
    if (thumbnailNodes.length > 0) {
        // Make sure one is visible for correct measurement
        thumbnailNodes[0].style.display = '';
        cardHeight = thumbnailNodes[0].offsetHeight + gapValue;
    }

    // How many fit?
    const maxVisible = cardHeight > 0 ? Math.floor(availableHeight / cardHeight) : thumbnailNodes.length;

    // Only show those that fit
    thumbnailNodes.forEach((node, i) => {
        node.style.display = i < maxVisible ? '' : 'none';
    });

    // Show "View More" if some were hidden
    const overflow = maxVisible < thumbnailNodes.length;
    if (viewMoreBtn) viewMoreBtn.style.display = overflow ? 'block' : 'none';
}

// Debounced event handlers
function debounce(func, delay) {
    let timer;
    return function () {
        clearTimeout(timer);
        timer = setTimeout(func, delay);
    }
}

window.addEventListener('resize', debounce(calculateThumbnailsVisibility, 200));
window.addEventListener('load', calculateThumbnailsVisibility);
